// built-from: /engagement-repo-connection v1 impl:engagement-repo-connection role:implementation
// useKnowledgeRepoConnect — the API-boundary hook for the /knowledge-repo
// connect surface (feature 032-knowledge-repo-link; re-founded by feature
// 035-repo-connect-zero-paste; re-founded AGAIN by feature
// 072-engagement-repo-connection).
//
// The surface reaches the API ONLY through this hook (constitution III: never
// `fetch` inside a component). 072 replaces the zero-argument single-button
// connect with a URL + fine-grained PAT the consultant supplies once per
// engagement: `connect({ url, token })` POSTs that body verbatim to
// /api/engagements/{id}/repoLink/connect. The server-side connect route ALONE
// links the repo (it seals the token and persists the linked descriptor) — so
// per the Architect ruling's mandatory rework, this hook does NOT re-persist
// the descriptor via a follow-up saveRepoLink PUT; that would risk clobbering
// or re-injecting the sealed token from a stale client write. It never sees or
// stores the plaintext token beyond the one outgoing request.

import { useCallback, useState } from 'react';
import { useEngagement } from './useEngagement';

export type ConnectErrorCode =
  | 'TOKEN_REQUIRED'
  | 'INVALID_URL'
  | 'NOT_CONFIGURED'
  | 'NO_PERMISSION'
  | 'REPO_CREATE_FAILED';

export type ConnectStatus = 'idle' | 'connecting' | 'error' | 'linked';

export interface ConnectInput {
  url: string;
  token: string;
}

export interface LinkedRepoDescriptor {
  status: 'linked';
  host?: string;
  owner: string;
  repo: string;
  url?: string;
  linkedAt?: string;
}

export interface UseKnowledgeRepoConnectReturn {
  status: ConnectStatus;
  error: { code: string } | null;
  linked: LinkedRepoDescriptor | null;
  connect: (input: ConnectInput) => Promise<void>;
}

// POST the connect route with { url, token }. Resolves the linked descriptor
// (already stripped of the token server-side) on success, or a typed error
// code on a 4xx. Never throws — any other failure resolves a generic code.
async function postConnect(
  id: string,
  input: ConnectInput,
): Promise<{ ok: true; descriptor: LinkedRepoDescriptor } | { ok: false; code: string }> {
  try {
    const res = await fetch(`/api/engagements/${encodeURIComponent(id)}/repoLink/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: input.url, token: input.token }),
    });
    const body = await res.json().catch(() => null);
    if (res.ok && body && body.status === 'linked') {
      return { ok: true, descriptor: body as LinkedRepoDescriptor };
    }
    const code = (body && (body.code || body.error)) || 'CONNECT_FAILED';
    return { ok: false, code: String(code) };
  } catch {
    return { ok: false, code: 'CONNECT_FAILED' };
  }
}

export function useKnowledgeRepoConnect(): UseKnowledgeRepoConnectReturn {
  const { currentEngagementId } = useEngagement();
  const [status, setStatus] = useState<ConnectStatus>('idle');
  const [error, setError] = useState<{ code: string } | null>(null);
  const [linked, setLinked] = useState<LinkedRepoDescriptor | null>(null);

  const connect = useCallback(
    async (input: ConnectInput): Promise<void> => {
      // No double-submit: a connect already in flight ignores further calls.
      setStatus((prev) => prev);
      setStatus('connecting');
      setError(null);

      const id = currentEngagementId;
      if (!id) {
        setStatus('error');
        setError({ code: 'NO_ENGAGEMENT' });
        return;
      }

      const result = await postConnect(id, input);
      if (!result.ok) {
        setStatus('error');
        setError({ code: result.code });
        return;
      }

      // 072: the connect route ALONE links the repo — no follow-up PUT, so the
      // sealed token can never be clobbered or re-injected from the client.
      setLinked(result.descriptor);
      setStatus('linked');
    },
    [currentEngagementId],
  );

  return { status, error, linked, connect };
}
